import { Button, CardBackground, Typography } from "@hellocarbo/lasagne";
import {
  ErrorBoundary as RollbarErrorBoundary,
  useRollbar,
} from "@rollbar/react";
import { use, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  ErrorResponse,
  isRouteErrorResponse,
  Link,
  useRouteError,
} from "react-router";

import background from "@/assets/error-background.png";
import PageWrapper from "@/components/ui/PageWrapper";
import { UserContext } from "@/contexts";

type HANDLED_STATUS = "403" | "404" | "5xx" | "unknown";

function getErrorResponseStatus(error: ErrorResponse): HANDLED_STATUS {
  if ([403, 404].includes(error.status))
    return error.status.toString() as HANDLED_STATUS;
  if (error.status >= 500 && error.status < 600) return "5xx";
  return "unknown";
}

function ErrorBoundary() {
  const error = useRouteError();
  const rollbar = useRollbar();
  const { t } = useTranslation("error");
  const { user } = use(UserContext);

  const isRouteErrorResp = isRouteErrorResponse(error); // error generated by a 4xx/5xx response thrown from a loader/action
  const code = isRouteErrorResp
    ? getErrorResponseStatus(error as ErrorResponse)
    : "unknown";

  useEffect(() => {
    if (isRouteErrorResp) {
      rollbar.error(error.data, error); // error.data contains the error message
    } else {
      rollbar.error(error as Error);
    }
  }, [error, rollbar, isRouteErrorResp]);

  return (
    <RollbarErrorBoundary>
      <PageWrapper className={user ? "" : "w-screen"}>
        <div className="relative z-0 flex h-screen">
          <CardBackground className="absolute top-1/2 left-1/2 w-5/6 -translate-x-1/2 -translate-y-1/2 text-center lg:mx-0 lg:max-w-[560px]">
            {isRouteErrorResp && code !== "unknown" ? (
              <div>
                <Typography step="5" as="h1" weight="bold" className="mb-xs">
                  {t(`error:knownError.${code}.title`)}
                </Typography>
                <Typography weight="semibold" className="text-grayscale">
                  {t(`error:knownError.${code}.text`)}
                </Typography>
              </div>
            ) : (
              <div>
                <Typography step="5" as="h1" weight="bold" className="mb-xs">
                  {t("error:unknownError.thereIsAnError")}
                </Typography>
                <Typography weight="semibold" className="text-grayscale">
                  {t("error:unknownError.text")}
                </Typography>
                {(error as Error)?.message &&
                (error as Error)?.stack &&
                import.meta.env.MODE === "development" ? (
                  <div className="bg-negative-xx-light mt-md p-sm max-h-[50vh] overflow-auto rounded-md shadow-inner">
                    <Typography
                      weight="semibold"
                      step="0"
                      className="text-negative-dark p-sm sticky top-0 left-0 z-10 block rounded-md bg-white shadow-sm"
                    >
                      {(error as Error).message}
                    </Typography>
                    <Typography
                      weight="semibold"
                      step="-1"
                      className="text-grayscale mt-md"
                      as="pre"
                    >
                      {(error as Error).stack}
                    </Typography>
                  </div>
                ) : null}
              </div>
            )}
            {user && (
              <Button shape="soft" className="mt-lg" as={Link} to="/">
                {t("error:backToDashboard")}
              </Button>
            )}
          </CardBackground>
          <img
            src={background}
            className="absolute top-0 left-0 z-[-1] h-full w-full object-cover"
          />
        </div>
      </PageWrapper>
    </RollbarErrorBoundary>
  );
}

export default ErrorBoundary;
